/**
 * Period selector: Since 14 Jun [default] / Today / This week / This month /
 * Quarter-to-date / Custom range.
 * Uses the retro-terminal segmented button style from the approved prototype.
 * touch-target: each button is ≥44px tall via min-height.
 *
 * "Since 14 Jun" (preset=since_cutoff) is the default per the reliability
 * design spec — the scorecard cohort is tasks STARTED on/after the 14-Jun
 * cutoff (board Start Date, falling back to created date while the property
 * is backfilled), so QTD is thin until more history accrues.
 */
import { Box, Stack, TextField, Typography } from '@mui/material';
import { type ReactElement, useState } from 'react';

import type { PeriodPreset, ScorecardParams } from '@/types/scorecard';

interface PeriodFilterProps {
  value: ScorecardParams;
  onChange: (params: ScorecardParams) => void;
}

const PRESETS: { preset: PeriodPreset; label: string }[] = [
  { preset: 'since_cutoff', label: 'Since 14 Jun' },
  { preset: 'today', label: 'Today' },
  { preset: 'week', label: 'This week' },
  { preset: 'month', label: 'This month' },
  { preset: 'qtd', label: 'Quarter to date' },
  { preset: 'custom', label: 'Custom' },
];

export function PeriodFilter({ value, onChange }: PeriodFilterProps): ReactElement {
  const [customStart, setCustomStart] = useState(value.start ?? '');
  const [customEnd, setCustomEnd] = useState(value.end ?? '');

  const active = value.preset ?? 'since_cutoff';

  function handlePreset(preset: PeriodPreset) {
    if (preset === 'custom') {
      onChange({ preset: 'custom', start: customStart || undefined, end: customEnd || undefined });
    } else {
      onChange({ preset });
    }
  }

  function handleCustomApply() {
    if (customStart && customEnd) {
      onChange({ preset: 'custom', start: customStart, end: customEnd });
    }
  }

  return (
    <Stack spacing={0.75}>
      <Typography
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.625rem',
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
          color: 'text.faintest',
        }}
      >
        Period
      </Typography>

      {/* Segmented control */}
      <Box
        sx={{
          display: 'inline-flex',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '6px',
          background: 'background.deeper',
          p: '3px',
          gap: '2px',
          flexWrap: 'wrap',
        }}
        role="group"
        aria-label="Select time period"
      >
        {PRESETS.map(({ preset, label }) => {
          const isActive = active === preset;
          return (
            <Box
              key={preset}
              component="button"
              onClick={() => handlePreset(preset)}
              aria-pressed={isActive}
              aria-label={`Select period: ${label}`}
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.2px',
                px: 1.625,
                minHeight: 44,
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'background 140ms, color 140ms',
                // active state
                ...(isActive
                  ? {
                      background: 'rgba(217, 119, 87, 0.10)',
                      color: 'primary.main',
                      boxShadow: 'inset 0 0 0 1px rgba(217, 119, 87, 0.30)',
                    }
                  : {
                      background: 'transparent',
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary' },
                    }),
                // Custom gets a trailing chevron indicator
                ...(preset === 'custom'
                  ? { '&::after': { content: '" ▾"', opacity: 0.6, fontSize: '0.6rem' } }
                  : {}),
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>

      {/* Custom date range inputs — shown only when custom is selected */}
      {active === 'custom' && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Custom period start date' }}
            sx={{ width: 150 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Custom period end date' }}
            sx={{ width: 150 }}
          />
          <Box
            component="button"
            onClick={handleCustomApply}
            disabled={!customStart || !customEnd}
            aria-label="Apply custom date range"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
              px: 1.625,
              minHeight: 44,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '5px',
              background: 'background.deeper',
              color: 'text.secondary',
              cursor: 'pointer',
              transition: 'all 140ms',
              '&:hover': { borderColor: '#353540', color: 'text.primary' },
              '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
            }}
          >
            Apply
          </Box>
        </Stack>
      )}
    </Stack>
  );
}
